import { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Eye, Users, FileText, Percent } from "lucide-react";
import { http } from "@/lib/api";

const RANGES = [7, 30, 90];
const PAGE_NAMES = { "/": "Home", "/services": "Services", "/about": "About", "/contact": "Contact / Quote" };

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-[var(--line)] bg-[var(--char)] p-5" data-testid={testId}>
    <p className="flex items-center gap-2 font-mono-x text-[12px] uppercase tracking-[0.14em] text-zinc-400"><Icon className="w-4 h-4 text-[var(--orange)]" />{label}</p>
    <p className="mt-2 font-display font-black text-4xl">{value}</p>
  </div>
);

const BarList = ({ title, rows, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="border border-[var(--line)] bg-[var(--char)] p-5" data-testid={testId}>
      <p className="font-mono-x text-[12px] uppercase tracking-[0.14em] text-zinc-300 mb-4">{title}</p>
      {rows.length === 0 && <p className="text-sm text-zinc-400">No data yet.</p>}
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.label} className="relative">
            <div className="absolute inset-y-0 left-0 bg-[var(--orange)]/15" style={{ width: `${(r.value / max) * 100}%` }} />
            <div className="relative flex justify-between gap-3 px-3 py-2 text-sm">
              <span className="text-zinc-200 truncate">{r.label}</span>
              <span className="text-zinc-300 shrink-0 font-mono-x text-xs">{r.extra}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export const Analytics = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    http.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).catch(() => setData({}));
  }, [days]);

  const t = data?.totals ?? {};
  const series = Array.isArray(data?.series) ? data.series.map((s) => ({ ...s, label: fmtDay(s.day) })) : [];
  const pages = (data?.pages ?? []).map((p) => ({ label: PAGE_NAMES[p.path] || p.path, value: p.views, extra: `${p.views} views · ${p.visitors} visitors` }));
  const sources = (data?.sources ?? []).map((s) => ({ label: s.source, value: s.visits, extra: `${s.visits} visits · ${s.quotes} quotes` }));
  const devices = (data?.devices ?? []).map((d) => ({ label: d.device, value: d.visits, extra: `${d.visits} visits` }));

  return (
    <section className="mt-8" data-testid="analytics-section">
      <div className="flex gap-2 mb-6" data-testid="analytics-range">
        {RANGES.map((r) => (
          <button key={r} onClick={() => setDays(r)} data-testid={`analytics-range-${r}`}
            className={`px-4 py-2 font-mono-x text-xs uppercase tracking-wider border transition-colors ${days === r ? "bg-[var(--orange)] text-[var(--ink)] border-[var(--orange)]" : "border-[var(--line)] text-zinc-300 hover:border-zinc-500"}`}>
            {r} days
          </button>
        ))}
      </div>
      {data === null ? <p className="text-zinc-400">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={Eye} label="Page views" value={t.views ?? 0} testId="stat-views" />
            <Stat icon={Users} label="Visitors" value={t.visitors ?? 0} testId="stat-visitors" />
            <Stat icon={FileText} label="Quote requests" value={t.quotes ?? 0} testId="stat-quotes" />
            <Stat icon={Percent} label="Visitors → quote" value={`${t.conversion ?? 0}%`} testId="stat-conversion" />
          </div>
          <div className="mt-4 border border-[var(--line)] bg-[var(--char)] p-4 h-[300px]" data-testid="analytics-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#2c3037" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={20} />
                <YAxis allowDecimals={false} tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#16181c", border: "1px solid #2c3037", color: "#fff" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="views" name="Page views" stroke="#ff5a1f" fill="#ff5a1f" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#e4e4e7" fill="#e4e4e7" fillOpacity={0.05} strokeWidth={1.5} />
                <Bar dataKey="quotes" name="Quotes" fill="#22c55e" barSize={8} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <BarList title="Top pages" rows={pages} testId="analytics-pages" />
            <BarList title="Where visitors come from" rows={sources} testId="analytics-sources" />
            <BarList title="Devices" rows={devices} testId="analytics-devices" />
          </div>
          <p className="mt-3 text-xs text-zinc-400">Visitors are counted per browser. Your own visits to /admin are not counted.</p>
        </>
      )}
    </section>
  );
};
